import React, { useCallback, useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";

import "../DamagesVideoAnalysis.scss";
import LoadingSpinner from "../../../../components/loading-spinner/LoadingSpinner";
import Svg from "../../../../components/svg/Svg";
import { PubSubEvents } from "../../../../services/PubSubEvents";
import { RunVideoAnalysis } from "../../../../services/authorization/Permissions";
import { permissionsService } from "../../../../services/authorization/UserPermissionsService";
import { DamagesStorage } from "../../../../services/damage/DamagesStorage";
import { translationService } from "../../../../services/translation/TranslationService";
import useTheme from "../../../../services/theme-service/ThemeService";
import "./VideoAnalysisDiagram.scss";
import OverviewPlot from "./OverviewPlot";
import CharacterizationsPlot from "./CharacterizationsPlot";
import { Info, Warning } from "../../../../components/svg/svgImageList";
import { videoPlayerService } from "../../../../services/video-player/VideoPlayerService";
import { globalModule } from "../../../../services/service-provider/GlobalModule";
import { Video } from "../../../../services/inspection/Video";
import { AnalysisService } from "../../../../services/analysis/AnalysisService";

export interface VideoAnalysisDiagramProps {
  videoAnalysis: AnalysisService;
  onVAFinished: (fileId:number) => void;
  damagesStorage: DamagesStorage;
  video?: Video;
  readonly?: boolean;
  canAnalyze?: boolean;
}

export default observer(function DamagesVideoAnalysis(props: VideoAnalysisDiagramProps) {
  const { videoAnalysis, damagesStorage, onVAFinished, video, readonly } =
    props;
  const { themeClass } = useTheme();

  const videoAnalysisNotYetStarted = useMemo(() => {
    return (
      !videoAnalysis.isLoading &&
      videoAnalysis?.analysisNotStarted &&
      !videoAnalysis?.analysisRunning
    );
  }, [
    videoAnalysis?.analysisNotStarted,
    videoAnalysis?.analysisRunning,
    videoAnalysis.isLoading,
  ]);

  const handleDoubleClick = useCallback((time:number) => {
    if (readonly) return;
    globalModule.videoService.addDamage(time);
  }, [readonly]);

  const handleRetryAnalysis = useCallback(async () => {
    await videoAnalysis.startService();
    await videoAnalysis.load();
  }, [videoAnalysis])

  useEffect(() => {
    var analysisSubscribeToken = PubSub.subscribe(
      PubSubEvents.VideoAnalysisFinished,
      async (_event: string, notification: Notification) => {
        onVAFinished(notification.data.fileId);
      }
    );

    return function cleanup() {
      PubSub.unsubscribe(analysisSubscribeToken);
    };
  }, [onVAFinished]);

  return (
    <>
      <div
        className={themeClass(
          ["video-analysis-diagram"],
          [videoAnalysisNotYetStarted ? "not-started" : ""]
        )}
      >
        {videoAnalysis.isLoading && <LoadingSpinner />}
        {video?.isConverting &&
          videoAnalysis?.analysisRunning && (
            <div className={themeClass(["video-not-available"])}>
              <LoadingSpinner spinnerOnly />
              <p className={themeClass(["message"])}>
                {translationService.translate(
                  "damages.DamagesVideoAnalysis.video-analysis-format-message"
                )}
              </p>
              <p className={themeClass(["message"])}>
                {translationService.translate(
                  "damages.DamagesVideoAnalysis.video-analysis-format-message-automatic-start"
                )}
              </p>
            </div>
          )}
        {video?.conversionFailed &&
          videoAnalysis?.analysisRunning && (
            <>
              <div className={themeClass(["video-failed-conversion"])}>
                <div className="svg-warning">
                  <Svg svg={Warning} width={100} />
                </div>
                {props.canAnalyze ? (
                  <>
                    <p className={themeClass(["message"])}>
                      {translationService.translate(
                        "damages.DamagesVideoAnalysis.video-analysis-failed-conversion-admin-user-message"
                      )}
                    </p>
                    <p className={themeClass(["message"])}>
                      {translationService.translate(
                        "damages.DamagesVideoAnalysis.video-analysis-failed-conversion-retry"
                      )}
                    </p>
                    <p className={themeClass(["message"])}>
                      {` ${translationService.translate(
                        "damages.DamagesVideo.video-conversion-error-warning-contact"
                      )} `}
                    </p>
                  </>
                ) : (
                  <p className={themeClass(["message"])}>
                    {translationService.translate(
                      "damages.DamagesVideoAnalysis.video-analysis-failed-conversion-guest-message"
                    )}
                  </p>
                )}
              </div>
            </>
          )}
        {videoAnalysisNotYetStarted && (
          <div className="message-info">
            <Svg className="svg-info" svg={Info} />
            <span>
              {translationService.translate(
                "damages.DamagesVideoAnalysis.video-analysis-not-started"
              )}
            </span>
          </div>
        )}
        {videoAnalysis?.analysisRunning &&
          video?.isAvailable && (
            <>
              <LoadingSpinner />
              <p className="centered">
                {translationService.translate(
                  "damages.DamagesVideoAnalysis.video-analysis-in-progress"
                )}
              </p>
            </>
          )}
        {!videoAnalysis.isLoading &&
          !videoAnalysis.analysisRunning &&
          videoAnalysis?.analysisSuccessful && (
            <div className="va-diagram-container">
              <div className="va-graph-container">
                <div className="va-title">
                  {translationService.translate("PlanUsage.video-analysis")}
                </div>
                {!videoPlayerService.videoDuration ? (
                  <LoadingSpinner />
                ) : (
                  <div className="va-plot-container">
                    {videoAnalysis.isOverviewDisplayed ? (
                      <OverviewPlot
                        videoAnalysisService={videoAnalysis}
                        damagesStorage={damagesStorage}
                        onDoubleClick={handleDoubleClick}
                      />
                    ) : (
                      <CharacterizationsPlot
                        videoAnalysisService={videoAnalysis}
                        damagesStorage={damagesStorage}
                        onDoubleClick={handleDoubleClick}
                      />
                    )}
                  </div>
                )}
                {!readonly &&
                <div className="damage-labels-container">
                  <div>
                    {translationService.translate("damages.DamagesVideoAnalysis.added-damages")}
                  </div>
                  <div className="info-container">
                    <Svg className="svg-info" svg={Info} />
                    {translationService.translate(
                      "damages.DamagesVideoAnalysis.double-click-information"
                    )}
                  </div>
                </div>
                }
              </div>
            </div>
          )}
        {!videoAnalysis.isLoading &&
          !videoAnalysis.analysisRunning &&
          videoAnalysis?.analysisFailed && (
            <div className={themeClass(["video-failed-analysis"])}>
              <div className="svg-warning">
                <Svg svg={Warning} width={100} />
              </div>
              <br />
              <p className={themeClass(["message"])}>
                {`${translationService.translate(
                  "damages.DamagesVideoAnalysis.video-analysis-failed"
                )} `}
                {props.canAnalyze && (
                  <>
                    <span onClick={handleRetryAnalysis}>
                    {translationService.translate(
                      "damages.DamagesVideoAnalysis.video-analysis-failed.run-again"
                    )}
                    </span>
                    <p className={themeClass(["message"])}>
                      {`${translationService.translate(
                        "damages.DamagesVideo.video-analysis-error-warning-contact"
                      )} `}
                    </p>
                  </>
                )}
                {!props.canAnalyze &&
                  translationService.translate(
                    "damages.DamagesVideoAnalysis.video-analysis-failed-guest-user-message"
                  )}
              </p>
              <br />
              <p className={themeClass(["message", "margin"])}> {props.videoAnalysis.errorMessage}</p>
            </div>
          )}
      </div>
    </>
  );
});
